import { isFeatureActiveOnCurrentFarm } from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CropFCPO,
  INCENTIVE_NAME,
  Incentive,
  IncentiveName,
  PRACTICE_SLUG,
  PracticeBundle,
  PracticeSlug,
  RequirementLevel,
} from "models";

type FormInput = "cropName" | "dates" | "yield" | "fertilizerManagement" | "tillageDepth" | Exclude<PracticeSlug, "crops"> | "speciesCount";
export type InputRequirementConfig = { [k in FormInput]?: RequirementLevel };

const DIVERSE_COVER_CROP_INCENTIVE_NAMES: IncentiveName[] = [INCENTIVE_NAME.diverseWinterCoverCrops, INCENTIVE_NAME.diverseCatchCrops];

export const getDiverseCoverCropIncentive = (carbonProgram: CarbonProgram | null | undefined): Incentive | undefined => {
  const cropsConfig = carbonProgram?.fieldsManagement.practices?.find((p) => p.slug === PRACTICE_SLUG.CROPS);
  return cropsConfig?.incentives?.find(({ name }) => DIVERSE_COVER_CROP_INCENTIVE_NAMES.includes(name));
};

export const isRequired = (level?: RequirementLevel) => level === "requiredForProgram" || level === "requiredForSaving";
export const isRequiredForSaving = (level?: RequirementLevel) => level === "requiredForSaving";
// NOTE: If the input key does not exist in the requirement config we completely hide the input even if optional inputs are shown
export const shouldShowInput = (level?: RequirementLevel, optionalInputsAreShown?: boolean) =>
  isRequired(level) || (level === "optional" && optionalInputsAreShown) || level === "optionalAlwaysShown";

/**
 * Function which returns the configuration which inputs are required for saving form, required for program or optional.
 * If the configuration does not include an input key, it will be hidden always.
 *
 * @param carbonProgram carbon program configuration needed to check if process based model is used as algorithm
 * @param availablePracticeBundles available practice bundles for the field
 * @param crop currently selected crop needed to check crop type
 * @param fieldIsEnrolled whether the field is enrolled in a program
 * @returns requirement configuration -> object with input key mapping to requirement level
 */
export const getInputRequirementConfig = (
  carbonProgram: CarbonProgram | null,
  availablePracticeBundles: PracticeBundle[],
  crop: CropFCPO,
  fieldIsEnrolled: boolean,
  ignoreCarbonProgramConditions = false
): InputRequirementConfig | null => {
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  // Default config has other practices as optional (even if they are not even part of the program, because they don't affect payout)
  const config: InputRequirementConfig = {
    cropName: "requiredForSaving",
    dates: "requiredForSaving",
    mineralFertilizers: "optional",
    organicFertilizers: "optional",
    seedings: "optional",
    tillages: "optional",
  };

  if (crop.cropType === "UNDERSOWING") {
    return {
      cropName: "requiredForSaving",
      dates: "requiredForSaving",
    };
  }

  // no carbon-program conditions should gate anything here, so every practice/input is freely editable
  if (ignoreCarbonProgramConditions) {
    return {
      cropName: "requiredForSaving",
      dates: "requiredForSaving",
      mineralFertilizers: "optionalAlwaysShown",
      organicFertilizers: "optionalAlwaysShown",
      seedings: "optionalAlwaysShown",
      tillages: "optionalAlwaysShown",
      tillageDepth: "optionalAlwaysShown",
      fertilizerManagement: "optionalAlwaysShown",
      speciesCount: "optionalAlwaysShown",
      ...(crop.cropType === CROP_TYPE_FCPO.MAIN ? { yield: "optionalAlwaysShown" } : {}),
    };
  }

  if (carbonProgram && carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    const practiceMap = new Map(carbonProgram?.fieldsManagement.practices?.map((p) => [p.slug, p]));
    const organicFertilizerConfig = practiceMap.get(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
    const tillageConfig = practiceMap.get(PRACTICE_SLUG.TILLAGES);

    const conservationTillageIncentive = tillageConfig?.incentives?.find(({ name }) => name === INCENTIVE_NAME.conservationTillage);
    const organicFertilizerIncentive = organicFertilizerConfig?.incentives?.find(({ name }) => name === INCENTIVE_NAME.fourR);
    const organicFertilizerIncentiveConfig = organicFertilizerIncentive
      ? {
          // 4R should never be optional, but completely hidden if it doesn't exist
          ...(crop.cropType === CROP_TYPE_FCPO.MAIN && !!organicFertilizerIncentive?.requirementLevel
            ? { fertilizerManagement: organicFertilizerIncentive.requirementLevel }
            : {}),
        }
      : {};

    return {
      // practices
      ...config,
      ...(organicFertilizerConfig?.required ? { organicFertilizers: "requiredForProgram" } : {}),
      ...(crop.cropType === CROP_TYPE_FCPO.MAIN ? { yield: "requiredForProgram" } : {}),

      // incentives
      ...organicFertilizerIncentiveConfig,
      ...(conservationTillageIncentive ? { tillageDepth: "requiredForSaving", tillages: tillageConfig ? "requiredForProgram" : "optional" } : {}),
      ...(getDiverseCoverCropIncentive(carbonProgram) ? { speciesCount: "requiredForProgram" } : {}),
    };
  }

  // If there is no carbon program (in that case we only should have noe carbon program result)  we just fall back to showing everything
  // If field is not enrolled we also want to show all inputs
  if (!carbonProgram || !fieldIsEnrolled) {
    return {
      cropName: "requiredForSaving",
      dates: "requiredForSaving",
      mineralFertilizers: "requiredForProgram",
      organicFertilizers: "requiredForProgram",
      seedings: "requiredForProgram",
      tillages: "requiredForProgram",
      tillageDepth: "requiredForProgram",
      ...(crop.cropType === CROP_TYPE_FCPO.MAIN ? { yield: "optionalAlwaysShown" } : {}),
    };
  }

  if (crop.cropType === CROP_TYPE_FCPO.MAIN) {
    config.yield = isSoilPlus3DataGapFeatureActive ? "requiredForProgram" : "optionalAlwaysShown";
  }

  // It should never be optional, but completely hidden if there is no tillage incentive
  if (isSoilPlus3DataGapFeatureActive) {
    config.tillageDepth = "requiredForSaving";
  }

  // Loop through practice keys and figure out which requirement they have (this will then override the optional state possibly)
  availablePracticeBundles.forEach((bundle) => {
    if (bundle.slug === PRACTICE_SLUG.CROPS) {
      return; // No need to look at crops, because the crop name and dates are already defined as requiredForSaving anyway
    }

    config[bundle.slug] = bundle.active ? "requiredForProgram" : "optional";
  });

  return config;
};
